import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import 'dayjs/locale/pt-br.js';
import { scheduleService } from '../services/scheduleService.js';
import { formatPhone, getSpeciesLabel } from '../utils/formatters.js';
import toast from 'react-hot-toast';
import placeholderImage from '../images/placeholder.png';
import './ScheduleDetailsModal.css';
import './Dashboard.css';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('pt-br');

function ScheduleDetailsModal({ scheduleId, onClose, onStatusChange }) {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [hoveredItem, setHoveredItem] = useState(null);

  useEffect(() => {
    if (scheduleId) {
      loadScheduleDetails();
    }
  }, [scheduleId]);

  const loadScheduleDetails = async () => {
    setLoading(true);
    const result = await scheduleService.getScheduleDetails(scheduleId);
    
    if (result.success) {
      setSchedule(result.schedule);
    } else {
      toast.error(result.error || 'Erro ao carregar detalhes');
      onClose();
    }
    setLoading(false);
  };

  const handleStatusChange = async (newStatus, notes = null) => {
    const result = await scheduleService.updateScheduleStatus(scheduleId, newStatus, notes);
    
    if (result.success) {
      const messages = {
        confirmed: 'Agendamento confirmado!',
        rejected: 'Agendamento recusado',
        canceled: 'Agendamento cancelado',
        completed: 'Atendimento marcado como realizado!'
      };
      toast.success(messages[newStatus] || 'Status atualizado!');
      setSchedule({ ...schedule, status: newStatus, entityNotes: notes || schedule.entityNotes });
      if (onStatusChange) {
        onStatusChange(scheduleId, newStatus);
      }
    } else {
      toast.error(result.error || 'Erro ao atualizar status');
    }
  };

  const handleRejectClick = () => {
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      toast.error('Por favor, informe o motivo da recusa');
      return;
    }
    await handleStatusChange('rejected', rejectReason);
    setShowRejectModal(false);
    setRejectReason('');
  };

  const handleRejectCancel = () => {
    setShowRejectModal(false);
    setRejectReason('');
  };

  const handleCall = () => {
    if (schedule?.user?.phone) {
      window.location.href = `tel:${schedule.user.phone}`;
    }
  };

  const handleWhatsApp = () => {
    if (schedule?.user?.phone) {
      const phone = schedule.user.phone.replace(/\D/g, '');
      const message = encodeURIComponent(
        `Olá ${schedule.user.name}, sobre o agendamento do dia ${formatDate(schedule.dateTime)} às ${formatTime(schedule.dateTime)}.`
      );
      window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    }
  };

  const handleCopyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado para a área de transferência!`);
    } catch (err) {
      toast.error('Erro ao copiar texto');
    }
  };

  const formatDate = (dateTime) => {
    return dayjs(dateTime).format('ddd., D MMM YYYY');
  };

  const formatTime = (dateTime) => {
    return dayjs(dateTime).format('HH:mm');
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        label: 'Pendente',
        color: '#F59E0B',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        )
      },
      confirmed: {
        label: 'Confirmado',
        color: '#10B981',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        )
      },
      rejected: {
        label: 'Recusado',
        color: '#EF4444',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        )
      },
      canceled: {
        label: 'Cancelado',
        color: '#EF4444',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        )
      },
      completed: {
        label: 'Concluído',
        color: '#6366F1',
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        )
      }
    };
    return configs[status] || configs.pending;
  };

  const calculateAge = (birthday) => {
    if (!birthday) return '0 ano(s)';
    const years = dayjs().diff(dayjs(birthday), 'year');
    const months = dayjs().diff(dayjs(birthday), 'month') % 12;
    
    if (years === 0) {
      return `${months} mês${months !== 1 ? 'es' : ''}`;
    } else if (months === 0) {
      return `${years} ano${years !== 1 ? 's' : ''}`;
    }
    return `${years} ano${years !== 1 ? 's' : ''} e ${months} mês${months !== 1 ? 'es' : ''}`;
  };

  const getSexLabel = (sex) => {
    return sex === 'male' ? 'Macho' : sex === 'female' ? 'Fêmea' : sex;
  };

  const getClinicalSignLabel = (sign) => {
    const labels = {
      vomiting: 'Vômito',
      diarrhea: 'Diarreia',
      cough: 'Tosse',
      sneeze: 'Espirro',
      itching: 'Coceira',
      skin_wounds: 'Feridas na pele',
      weight_loss: 'Perda de peso',
      lethargy: 'Letargia',
      breathing_difficulty: 'Dificuldade respiratória',
      eye_nasal_discharge: 'Secreção ocular/nasal',
      lameness: 'Claudicação',
      urinary_changes: 'Alterações urinárias',
      seizures: 'Convulsões'
    };
    return labels[sign] || sign;
  };

  const getFeedingTypeLabel = (type) => {
    const labels = {
      dry_food: 'Ração',
      natural: 'Natural',
      mixed_food: 'Mista',
      therapeutic: 'Terapêutico'
    };
    return labels[type] || type;
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      once: '1x',
      twice: '2x',
      three_times: '3x',
      four_times: '4x',
      daily: 'Diário',
      by_month: 'por mês',
      by_day: 'por dia',
      by_year: 'por ano'
    };
    return labels[freq] || freq;
  };

  const getIntensityLabel = (intensity) => {
    const labels = {
      normal: 'Normal',
      increased: 'Aumentado',
      decreased: 'Reduzido'
    };
    return labels[intensity] || intensity;
  };

  const getHabitatLabel = (habitat) => {
    const labels = {
      indoor: 'Interno',
      outdoor: 'Externo',
      mixed: 'Misto'
    };
    return labels[habitat] || habitat;
  };

  const getHousingLabel = (housing) => {
    const labels = {
      house: 'Casa',
      apartment: 'Apartamento',
      farm: 'Sítio/Fazenda'
    };
    return labels[housing] || housing;
  };

  const getTemperamentLabel = (temperament) => {
    const labels = {
      aggressive: 'Agressivo',
      sociable: 'Sociável',
      fearful: 'Medroso',
      anxious: 'Ansioso'
    };
    return labels[temperament] || temperament;
  };

  const getActivityLevelLabel = (level) => {
    const labels = {
      low: 'Baixo',
      medium: 'Médio',
      high: 'Alto'
    };
    return labels[level] || level;
  };

  const getSeverityLabel = (severity) => {
    const labels = {
      low: 'Baixo',
      mild: 'Leve',
      moderate: 'Moderada',
      high: 'Alto',
      severe: 'Grave'
    };
    return labels[severity] || severity;
  };

  const getAllergyTypeLabel = (type) => {
    const labels = {
      allergy: 'Alergia',
      intolerance: 'Intolerância',
      food_restriction: 'Restrição Alimentar'
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Ativo',
      inactive: 'Inativo',
      completed: 'Concluído',
      up_to_date: 'Em dia',
      expired: 'Vencido',
      pending: 'Pendente'
    };
    return labels[status] || status;
  };

  const parseClinicalSigns = (signs) => {
    if (!signs) return [];
    return signs.split(',').map(s => getClinicalSignLabel(s.trim()));
  };

  if (!scheduleId) return null;

  return (
    <div className="schedule-modal-overlay" onClick={onClose}>
      <div className="schedule-modal-content" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="schedule-modal-loading">
            <div className="spinner"></div>
            <p>Carregando detalhes...</p>
          </div>
        ) : schedule ? (
          <>
            {/* Header */}
            <div className="schedule-modal-header">
              <h2>Detalhes do Agendamento</h2>
              <button className="schedule-modal-close" onClick={onClose}>
                ✕
              </button>
            </div>

            {/* Status Badge */}
            <div className="schedule-modal-status-row">
              <div 
                className="schedule-status-badge"
                style={{ backgroundColor: getStatusConfig(schedule.status).color, width: '100%', justifyContent: 'center' }}
              >
                <span className="schedule-status-icon">
                  {getStatusConfig(schedule.status).icon}
                </span>
                <span className="schedule-status-label">
                  {getStatusConfig(schedule.status).label}
                </span>
              </div>
            </div>

            {/* User Info */}
            <div className="schedule-section">
              <div className="schedule-user-info">
                {schedule.user?.image ? (
                  <img 
                    src={schedule.user.image} 
                    alt={schedule.user.name}
                    className="schedule-user-avatar"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = placeholderImage;
                    }}
                  />
                ) : (
                  <div className="schedule-user-avatar-placeholder">
                    {schedule.user?.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <div className="schedule-user-details">
                  <h3>{schedule.user?.name}</h3>
                  <div className="schedule-user-contact-item">
                    <p 
                      className="schedule-user-clickable"
                      onClick={() => handleCopyToClipboard(schedule.user?.email, 'E-mail')}
                      onMouseEnter={() => setHoveredItem('email')}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      {schedule.user?.email}
                    </p>
                    {hoveredItem === 'email' && (
                      <div className="schedule-tooltip">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        Clique para copiar
                      </div>
                    )}
                  </div>
                  <div className="schedule-user-contact-item">
                    <p 
                      className="schedule-user-clickable"
                      onClick={() => handleCopyToClipboard(schedule.user?.phone, 'Telefone')}
                      onMouseEnter={() => setHoveredItem('phone')}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      {formatPhone(schedule.user?.phone)}
                    </p>
                    {hoveredItem === 'phone' && (
                      <div className="schedule-tooltip">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        Clique para copiar
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Pet Info */}
            {schedule.pet && (
              <div className="schedule-section">
                <div className="schedule-pet-card">
                  {schedule.pet.photo && (
                    <img 
                      src={schedule.pet.photo} 
                      alt={schedule.pet.name}
                      className="schedule-pet-photo"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = placeholderImage;
                      }}
                    />
                  )}
                  <div className="schedule-pet-info">
                    <div className="fa-card-title-row" style={{ marginBottom: 4 }}>
                      <h4 style={{ margin: 0 }}>{schedule.pet.name}</h4>
                      {(schedule.pet.id || schedule.petId) && (
                        <span className="ref-tag">Pet #{schedule.pet.id || schedule.petId}</span>
                      )}
                    </div>
                    <p className="schedule-pet-breed">
                      {schedule.pet.breed} • {getSexLabel(schedule.pet.sex)} • {calculateAge(schedule.pet.birthday)}
                    </p>
                    <div className="schedule-pet-details">
                      <span className="schedule-pet-detail-item">
                        <svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor">
                          <path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5s.3-86.2 32.6-96.8s70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7 .9 78.5 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7c-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2C84.9 480 64 459.1 64 433.3v-1.6c0-10.4 1.6-20.8 5.2-30.5zM421.8 282.7c-24.5-14-29.1-51.7-10.2-84.1s54-47.3 78.5-33.3s29.1 51.7 10.2 84.1s-54 47.3-78.5 33.3zM310.1 189.7c-32.3-10.6-46.9-53.9-32.6-96.8s52.1-69.1 84.4-58.5s46.9 53.9 32.6 96.8s-52.1 69.1-84.4 58.5z"/>
                        </svg>
                        {getSpeciesLabel(schedule.pet.species)}
                      </span>
                      {schedule.pet.weight && (
                        <span className="schedule-pet-detail-item">
                          <svg width="14" height="14" viewBox="0 0 56 50" fill="currentColor">
                            <circle cx="28" cy="9" r="6" fill="none" stroke="currentColor" strokeWidth="3"/>
                            <path d="M20 15 L14 19 L8 50 L48 50 L42 19 L36 15" fill="currentColor"/>
                            <text x="28" y="38" fontSize="16" fontWeight="bold" fill="white" textAnchor="middle">KG</text>
                          </svg>
                          {schedule.pet.weight} kg
                        </span>
                      )}
                      {schedule.pet.color && (
                        <span className="schedule-pet-detail-item">
                          <svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor">
                            <path d="M512 256c0 .9 0 1.8 0 2.7c-.4 36.5-33.6 61.3-70.1 61.3H344c-26.5 0-48 21.5-48 48c0 3.4 .4 6.7 1 9.9c2.1 10.2 6.5 20 10.8 29.9c6.1 13.8 12.1 27.5 12.1 42c0 31.8-21.6 60.7-53.4 62c-3.5 .1-7 .2-10.6 .2C114.6 512 0 397.4 0 256S114.6 0 256 0S512 114.6 512 256zM128 288a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm0-96a32 32 0 1 0 0-64 32 32 0 1 0 0 64zM288 96a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm96 96a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/>
                          </svg>
                          {schedule.pet.color}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Schedule Info */}
            <div className="schedule-section">
              <div className="fa-card-title-row" style={{ marginBottom: 14 }}>
                <h4 className="schedule-section-title" style={{ margin: 0 }}>Informações do Agendamento</h4>
                {schedule.id && <span className="ref-tag">Agend. #{schedule.id}</span>}
              </div>
              <div className="schedule-info-grid">
                <div className="schedule-info-item">
                  <div className="schedule-info-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </div>
                  <div className="schedule-info-content">
                    <span className="schedule-info-label">Data e Hora</span>
                    <span className="schedule-info-value">
                      {formatDate(schedule.dateTime)}
                    </span>
                    <span className="schedule-info-time">
                      {formatTime(schedule.dateTime)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            {(schedule.userNotes || schedule.entityNotes) && (
              <div className="schedule-section">
                <h4 className="schedule-section-title">Informações Adicionais</h4>
                {schedule.userNotes && (
                  <div className="schedule-notes-card">
                    <span className="schedule-notes-label">Observações do Cliente:</span>
                    <p className="schedule-notes-text">{schedule.userNotes}</p>
                  </div>
                )}
                {schedule.entityNotes && (
                  <div className="schedule-notes-card">
                    <span className="schedule-notes-label">Observações da Clínica:</span>
                    <p className="schedule-notes-text">{schedule.entityNotes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Pet Health Info */}
            {schedule.medicalData?.health_info && (schedule.medicalData.health_info.clinicalSigns || schedule.medicalData.health_info.veterinarianNotes || schedule.medicalData.health_info.neutered !== null || schedule.medicalData.health_info.microchip) && (
              <div className="schedule-section">
                <h4 className="schedule-section-title">Informações de Saúde</h4>
                {schedule.medicalData.health_info.clinicalSigns && (
                  <div className="schedule-health-item">
                    <span className="schedule-health-label">Sinais Clínicos:</span>
                    <span className="schedule-health-value">
                      {parseClinicalSigns(schedule.medicalData.health_info.clinicalSigns).join(', ')}
                    </span>
                  </div>
                )}
                {schedule.medicalData.health_info.neutered !== null && (
                  <div className="schedule-health-item">
                    <span className="schedule-health-label">Castrado:</span>
                    <span className="schedule-health-value">
                      {schedule.medicalData.health_info.neutered ? 'Sim' : 'Não'}
                      {schedule.medicalData.health_info.neuteredDate && ` (${dayjs(schedule.medicalData.health_info.neuteredDate).format('DD/MM/YYYY')})`}
                    </span>
                  </div>
                )}
                {schedule.medicalData.health_info.microchip && (
                  <div className="schedule-health-item">
                    <span className="schedule-health-label">Microchip:</span>
                    <span className="schedule-health-value">{schedule.medicalData.health_info.microchip}</span>
                  </div>
                )}
                {schedule.medicalData.health_info.veterinarianNotes && (
                  <div className="schedule-notes-card">
                    <span className="schedule-notes-label">Observações Veterinárias:</span>
                    <p className="schedule-notes-text">{schedule.medicalData.health_info.veterinarianNotes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Vaccinations */}
            {schedule.medicalData?.vaccination && schedule.medicalData.vaccination.length > 0 && (
              <div className="schedule-section">
                <h4 className="schedule-section-title">
                  <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg" style={{verticalAlign: 'middle', marginRight: '8px'}}>
                    <path d="M15.5039 0.496926L16.6289 1.62193L17.7539 2.74693C18.0844 3.07739 18.0844 3.61177 17.7539 3.93872C17.4234 4.26568 16.8891 4.26919 16.5621 3.93872L16.0348 3.41138L14.6918 4.75083L16.6254 6.68443C16.9559 7.01489 16.9559 7.54927 16.6254 7.87622C16.2949 8.20318 15.7605 8.20669 15.4336 7.87622L12.9023 5.34497L10.3711 2.81724C10.0406 2.48677 10.0406 1.95239 10.3711 1.62544C10.7016 1.29849 11.2359 1.29497 11.5629 1.62544L13.4965 3.55903L14.8395 2.21958L14.3086 1.69224C13.9781 1.36177 13.9781 0.827394 14.3086 0.500441C14.6391 0.173488 15.1734 0.169972 15.5004 0.500441L15.5039 0.496926ZM7.39336 5.72466L9.54141 3.57661C9.55195 3.58716 9.5625 3.60122 9.57656 3.61177L10.1391 4.17427L12.1078 6.14302L14.0766 8.11177L14.6391 8.67427C14.6496 8.68482 14.6602 8.69536 14.6742 8.70943L7.95938 15.4243C7.59023 15.7934 7.09102 16.0008 6.56719 16.0008H3.4418L1.44141 18.0047C1.11094 18.3352 0.576563 18.3352 0.24961 18.0047C-0.0773435 17.6743 -0.0808591 17.1399 0.24961 16.8129L2.25352 14.809V11.6872C2.25352 11.1633 2.46094 10.6641 2.83008 10.295L4.35234 8.77271L6.35625 10.7766C6.57422 10.9946 6.93281 10.9946 7.15078 10.7766C7.36875 10.5586 7.36875 10.2 7.15078 9.98208L5.14688 7.97818L6.60234 6.52271L8.60625 8.52661C8.82422 8.74458 9.18281 8.74458 9.40078 8.52661C9.61875 8.30864 9.61875 7.95005 9.40078 7.73208L7.39688 5.72818L7.39336 5.72466Z" fill="#7165FF"/>
                  </svg>
                  Vacinação
                </h4>
                <div className="pet-data-list">
                  {schedule.medicalData.vaccination.map((vaccine, index) => (
                    <div key={index} className="pet-data-card">
                      <div className="pet-data-header">
                        <span className="pet-data-name">{vaccine.vaccineName}</span>
                        <span className={`pet-data-status status-${vaccine.status}`}>
                          {getStatusLabel(vaccine.status)}
                        </span>
                      </div>
                      <div className="pet-data-details">
                        <div className="pet-data-detail-item">
                          <span className="detail-label">Aplicação:</span>
                          <span className="detail-value">{dayjs(vaccine.applicationDate).format('DD/MM/YYYY')}</span>
                        </div>
                        {vaccine.nextApplicationDate && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Próxima dose:</span>
                            <span className="detail-value">{dayjs(vaccine.nextApplicationDate).format('DD/MM/YYYY')}</span>
                          </div>
                        )}
                        {vaccine.lotNumber && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Lote:</span>
                            <span className="detail-value">{vaccine.lotNumber}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dewormings */}
            {schedule.medicalData?.deworming && schedule.medicalData.deworming.length > 0 && (
              <div className="schedule-section">
                <h4 className="schedule-section-title">
                  <svg width="21" height="17" viewBox="0 0 21 17" fill="none" xmlns="http://www.w3.org/2000/svg" style={{verticalAlign: 'middle', marginRight: '8px'}}>
                    <path d="M3.9375 2.625C3.00586 2.625 2.25 3.38086 2.25 4.3125V8.25H5.625V4.3125C5.625 3.38086 4.86914 2.625 3.9375 2.625ZM0 4.3125C0 2.13633 1.76133 0.375 3.9375 0.375C6.11367 0.375 7.875 2.13633 7.875 4.3125V12.1875C7.875 14.3637 6.11367 16.125 3.9375 16.125C1.76133 16.125 0 14.3637 0 12.1875V4.3125ZM19.5082 13.2914C19.2586 13.7238 18.675 13.752 18.3199 13.4004L11.7246 6.80508C11.373 6.45352 11.3977 5.86641 11.8336 5.6168C12.6563 5.1457 13.609 4.875 14.625 4.875C17.7328 4.875 20.25 7.39219 20.25 10.5C20.25 11.516 19.9793 12.4687 19.5082 13.2914ZM17.4164 15.3832C16.5937 15.8543 15.641 16.125 14.625 16.125C11.5172 16.125 9 13.6078 9 10.5C9 9.48398 9.2707 8.53125 9.7418 7.70859C9.99141 7.27617 10.575 7.24805 10.9301 7.59961L17.5254 14.1949C17.877 14.5465 17.8523 15.1336 17.4164 15.3832Z" fill="#7165FF"/>
                  </svg>
                  Vermifugação
                </h4>
                <div className="pet-data-list">
                  {schedule.medicalData.deworming.map((deworming, index) => (
                    <div key={index} className="pet-data-card">
                      <div className="pet-data-header">
                        <span className="pet-data-name">{deworming.product}</span>
                        <span className={`pet-data-status status-${deworming.status}`}>
                          {getStatusLabel(deworming.status)}
                        </span>
                      </div>
                      <div className="pet-data-details">
                        {deworming.dosage && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Dosagem:</span>
                            <span className="detail-value">{deworming.dosage}</span>
                          </div>
                        )}
                        <div className="pet-data-detail-item">
                          <span className="detail-label">Aplicação:</span>
                          <span className="detail-value">{dayjs(deworming.applicationDate).format('DD/MM/YYYY')}</span>
                        </div>
                        {deworming.frequency && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Frequência:</span>
                            <span className="detail-value">{getFrequencyLabel(deworming.frequency)}</span>
                          </div>
                        )}
                        {deworming.nextDueDate && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Próxima dose:</span>
                            <span className="detail-value">{dayjs(deworming.nextDueDate).format('DD/MM/YYYY')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Medications */}
            {schedule.medicalData?.medications && schedule.medicalData.medications.length > 0 && (
              <div className="schedule-section">
                <h4 className="schedule-section-title">
                  <svg width="21" height="17" viewBox="0 0 21 17" fill="none" xmlns="http://www.w3.org/2000/svg" style={{verticalAlign: 'middle', marginRight: '8px'}}>
                    <path d="M3.9375 2.625C3.00586 2.625 2.25 3.38086 2.25 4.3125V8.25H5.625V4.3125C5.625 3.38086 4.86914 2.625 3.9375 2.625ZM0 4.3125C0 2.13633 1.76133 0.375 3.9375 0.375C6.11367 0.375 7.875 2.13633 7.875 4.3125V12.1875C7.875 14.3637 6.11367 16.125 3.9375 16.125C1.76133 16.125 0 14.3637 0 12.1875V4.3125ZM19.5082 13.2914C19.2586 13.7238 18.675 13.752 18.3199 13.4004L11.7246 6.80508C11.373 6.45352 11.3977 5.86641 11.8336 5.6168C12.6563 5.1457 13.609 4.875 14.625 4.875C17.7328 4.875 20.25 7.39219 20.25 10.5C20.25 11.516 19.9793 12.4687 19.5082 13.2914ZM17.4164 15.3832C16.5937 15.8543 15.641 16.125 14.625 16.125C11.5172 16.125 9 13.6078 9 10.5C9 9.48398 9.2707 8.53125 9.7418 7.70859C9.99141 7.27617 10.575 7.24805 10.9301 7.59961L17.5254 14.1949C17.877 14.5465 17.8523 15.1336 17.4164 15.3832Z" fill="#7165FF"/>
                  </svg>
                  Medicamentos
                </h4>
                <div className="pet-data-list">
                  {schedule.medicalData.medications.map((med, index) => (
                    <div key={index} className="pet-data-card">
                      <div className="pet-data-header">
                        <span className="pet-data-name">{med.name}</span>
                        <span className={`pet-data-status status-${med.status}`}>
                          {getStatusLabel(med.status)}
                        </span>
                      </div>
                      <div className="pet-data-details">
                        {med.dosage && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Dosagem:</span>
                            <span className="detail-value">{med.dosage}</span>
                          </div>
                        )}
                        {med.frequencyTimes && med.frequencyType && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Frequência:</span>
                            <span className="detail-value">{getFrequencyLabel(med.frequencyTimes)} {getFrequencyLabel(med.frequencyType)}</span>
                          </div>
                        )}
                        {med.durationTimes && med.durationType && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Duração:</span>
                            <span className="detail-value">{med.durationTimes} {med.durationType === 'days' ? 'dias' : med.durationType === 'weeks' ? 'semanas' : med.durationType === 'months' ? 'meses' : med.durationType}</span>
                          </div>
                        )}
                        {med.startDate && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Início:</span>
                            <span className="detail-value">{dayjs(med.startDate).format('DD/MM/YYYY')}</span>
                          </div>
                        )}
                        {med.endDate && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Fim:</span>
                            <span className="detail-value">{dayjs(med.endDate).format('DD/MM/YYYY')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Allergies */}
            {schedule.medicalData?.allergies && schedule.medicalData.allergies.length > 0 && (
              <div className="schedule-section">
                <h4 className="schedule-section-title">
                  <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" style={{verticalAlign: 'middle', marginRight: '8px'}}>
                    <path d="M9.00004 0.375C9.49926 0.375 9.95981 0.638672 10.2129 1.07109L17.8067 14.0086C18.0633 14.4445 18.0633 14.9824 17.8137 15.4184C17.5641 15.8543 17.0965 16.125 16.5938 16.125H1.4063C0.903561 16.125 0.435983 15.8543 0.186373 15.4184C-0.0632361 14.9824 -0.0597205 14.441 0.193405 14.0086L7.78715 1.07109C8.04028 0.638672 8.50083 0.375 9.00004 0.375ZM9.00004 4.875C8.53247 4.875 8.15629 5.25117 8.15629 5.71875V9.65625C8.15629 10.1238 8.53247 10.5 9.00004 10.5C9.46762 10.5 9.8438 10.1238 9.8438 9.65625V5.71875C9.8438 5.25117 9.46762 4.875 9.00004 4.875ZM10.125 12.75C10.125 12.4516 10.0065 12.1655 9.79554 11.9545C9.58456 11.7435 9.29841 11.625 9.00004 11.625C8.70168 11.625 8.41553 11.7435 8.20455 11.9545C7.99357 12.1655 7.87505 12.4516 7.87505 12.75C7.87505 13.0484 7.99357 13.3345 8.20455 13.5455C8.41553 13.7565 8.70168 13.875 9.00004 13.875C9.29841 13.875 9.58456 13.7565 9.79554 13.5455C10.0065 13.3345 10.125 13.0484 10.125 12.75Z" fill="#EF4444"/>
                  </svg>
                  Alergias e Restrições
                </h4>
                <div className="pet-data-list">
                  {schedule.medicalData.allergies.map((allergy, index) => (
                    <div key={index} className={`pet-data-card alert-card${allergy.severityLevel ? ` severity-${allergy.severityLevel}` : ''}`}>
                      <div className="pet-data-header">
                        <span className="pet-data-name">{allergy.substanceOrFood}</span>
                        {allergy.severityLevel && (
                          <span className={`pet-data-severity severity-${allergy.severityLevel}`}>
                            {getSeverityLabel(allergy.severityLevel)}
                          </span>
                        )}
                      </div>
                      <div className="pet-data-details">
                        {allergy.type && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Tipo:</span>
                            <span className="detail-value">{getAllergyTypeLabel(allergy.type)}</span>
                          </div>
                        )}
                        {allergy.symptoms && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Sintomas:</span>
                            <span className="detail-value">{allergy.symptoms}</span>
                          </div>
                        )}
                        {allergy.notes && (
                          <div className="pet-data-detail-item">
                            <span className="detail-label">Observações:</span>
                            <span className="detail-value">{allergy.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nutrition */}
            {schedule.medicalData?.nutrition && (
              <div className="schedule-section">
                <h4 className="schedule-section-title">
                  <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" style={{verticalAlign: 'middle', marginRight: '8px'}}>
                    <path d="M0 6C0 4.75898 1.00898 3.75 2.25 3.75C2.26758 3.75 2.28867 3.75 2.30625 3.75C2.56641 2.4668 3.70195 1.5 5.0625 1.5C5.58984 1.5 6.08203 1.64414 6.50039 1.89375C6.96797 0.99375 7.91367 0.375 9 0.375C10.0863 0.375 11.032 0.99375 11.4996 1.89375C11.918 1.64414 12.4102 1.5 12.9375 1.5C14.298 1.5 15.4336 2.4668 15.6937 3.75C15.7113 3.75 15.7324 3.75 15.75 3.75C16.991 3.75 18 4.75898 18 6C18 6.41133 17.891 6.79453 17.6977 7.125H0.302344C0.108984 6.79453 0 6.41133 0 6ZM0 9.21328C0 8.68242 0.432422 8.25 0.963281 8.25H17.0367C17.5676 8.25 18 8.68242 18 9.21328C18 11.6918 16.4391 13.8082 14.2488 14.6309L14.1855 15.1406C14.1152 15.7031 13.6371 16.125 13.0676 16.125H4.92891C4.36289 16.125 3.88125 15.7031 3.81094 15.1406L3.74766 14.6344C1.56094 13.8082 0 11.6918 0 9.21328Z" fill="#7165FF"/>
                  </svg>
                  Nutrição
                </h4>
                <div className="pet-info-grid">
                  {schedule.medicalData.nutrition.feedingType && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Tipo de alimentação</span>
                      <span className="pet-info-value">{getFeedingTypeLabel(schedule.medicalData.nutrition.feedingType)}</span>
                    </div>
                  )}
                  {schedule.medicalData.nutrition.foodBrandType && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Marca</span>
                      <span className="pet-info-value">{schedule.medicalData.nutrition.foodBrandType}</span>
                    </div>
                  )}
                  {schedule.medicalData.nutrition.foodProduct && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Produto</span>
                      <span className="pet-info-value">{schedule.medicalData.nutrition.foodProduct}</span>
                    </div>
                  )}
                  {schedule.medicalData.nutrition.foodCategory && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Categoria</span>
                      <span className="pet-info-value">{schedule.medicalData.nutrition.foodCategory}</span>
                    </div>
                  )}
                  {schedule.medicalData.nutrition.feedingFrequency && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Frequência</span>
                      <span className="pet-info-value">{getFrequencyLabel(schedule.medicalData.nutrition.feedingFrequency)}</span>
                    </div>
                  )}
                  {schedule.medicalData.nutrition.appetite && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Apetite</span>
                      <span className="pet-info-value">{getIntensityLabel(schedule.medicalData.nutrition.appetite)}</span>
                    </div>
                  )}
                  {schedule.medicalData.nutrition.waterIntake && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Ingestão de água</span>
                      <span className="pet-info-value">{getIntensityLabel(schedule.medicalData.nutrition.waterIntake)}</span>
                    </div>
                  )}
                  {schedule.medicalData.nutrition.recentDietChanges && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Mudanças recentes na dieta</span>
                      <span className="pet-info-value">{schedule.medicalData.nutrition.recentDietChanges}</span>
                    </div>
                  )}
                  {schedule.medicalData.nutrition.snacksDetails && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Petiscos / Comida caseira</span>
                      <span className="pet-info-value">{schedule.medicalData.nutrition.snacksDetails}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Behavior */}
            {schedule.medicalData?.behavior && (
              <div className="schedule-section">
                <h4 className="schedule-section-title">
                  <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg" style={{verticalAlign: 'middle', marginRight: '8px'}}>
                    <path d="M6.46875 0.25C7.55508 0.25 8.4375 1.13242 8.4375 2.21875V16.2812C8.4375 17.3676 7.55508 18.25 6.46875 18.25C5.45273 18.25 4.61602 17.4801 4.51055 16.4887C4.32773 16.5379 4.13438 16.5625 3.9375 16.5625C2.69648 16.5625 1.6875 15.5535 1.6875 14.3125C1.6875 14.0523 1.7332 13.7992 1.81406 13.5672C0.752344 13.1664 0 12.1398 0 10.9375C0 9.81602 0.657422 8.8457 1.61016 8.3957C1.3043 8.0125 1.125 7.52734 1.125 7C1.125 5.9207 1.88438 5.0207 2.89688 4.79922C2.84063 4.60586 2.8125 4.39844 2.8125 4.1875C2.8125 3.13633 3.53672 2.25039 4.51055 2.0043C4.61602 1.01992 5.45273 0.25 6.46875 0.25ZM11.5312 0.25C12.5473 0.25 13.3805 1.01992 13.4895 2.0043C14.4668 2.25039 15.1875 3.13281 15.1875 4.1875C15.1875 4.39844 15.1594 4.60586 15.1031 4.79922C16.1156 5.01719 16.875 5.9207 16.875 7C16.875 7.52734 16.6957 8.0125 16.3898 8.3957C17.3426 8.8457 18 9.81602 18 10.9375C18 12.1398 17.2477 13.1664 16.1859 13.5672C16.2668 13.7992 16.3125 14.0523 16.3125 14.3125C16.3125 15.5535 15.3035 16.5625 14.0625 16.5625C13.8656 16.5625 13.6723 16.5379 13.4895 16.4887C13.384 17.4801 12.5473 18.25 11.5312 18.25C10.4449 18.25 9.5625 17.3676 9.5625 16.2812V2.21875C9.5625 1.13242 10.4449 0.25 11.5312 0.25Z" fill="#7165FF"/>
                  </svg>
                  Comportamento
                </h4>
                <div className="pet-info-grid">
                  {schedule.medicalData.behavior.generalTemperament && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Temperamento</span>
                      <span className="pet-info-value">{getTemperamentLabel(schedule.medicalData.behavior.generalTemperament)}</span>
                    </div>
                  )}
                  {schedule.medicalData.behavior.physicalActivityLevel && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Nível de atividade</span>
                      <span className="pet-info-value">{getActivityLevelLabel(schedule.medicalData.behavior.physicalActivityLevel)}</span>
                    </div>
                  )}
                  {schedule.medicalData.behavior.livesWithOtherAnimals !== null && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Convive com outros animais</span>
                      <span className="pet-info-value">{schedule.medicalData.behavior.livesWithOtherAnimals ? 'Sim' : 'Não'}</span>
                    </div>
                  )}
                  {schedule.medicalData.behavior.otherAnimalsDescription && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Convive com outros animais</span>
                      <span className="pet-info-value">{schedule.medicalData.behavior.otherAnimalsDescription}</span>
                    </div>
                  )}
                  {schedule.medicalData.behavior.reactionsToDifferentEnvironmentsOrPeople && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Reações a ambientes/pessoas</span>
                      <span className="pet-info-value">{schedule.medicalData.behavior.reactionsToDifferentEnvironmentsOrPeople}</span>
                    </div>
                  )}
                  {schedule.medicalData.behavior.identifiedBehaviorProblems && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Problemas de comportamento</span>
                      <span className="pet-info-value">{schedule.medicalData.behavior.identifiedBehaviorProblems}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Environment */}
            {schedule.medicalData?.environment && (
              <div className="schedule-section">
                <h4 className="schedule-section-title">
                  <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{verticalAlign: 'middle', marginRight: '8px'}}>
                    <path d="M21.2126 8.98242C21.2126 9.61523 20.6852 10.1109 20.0876 10.1109H18.9626L18.9872 15.743C18.9872 15.8379 18.9802 15.9328 18.9696 16.0277V16.5938C18.9696 17.3707 18.3403 18 17.5634 18H17.0009C16.9622 18 16.9235 18 16.8848 17.9965C16.8356 18 16.7864 18 16.7372 18H15.5946H14.7509C13.9739 18 13.3446 17.3707 13.3446 16.5938V15.75V13.5C13.3446 12.8777 12.8419 12.375 12.2196 12.375H9.96961C9.34735 12.375 8.84461 12.8777 8.84461 13.5V15.75V16.5938C8.84461 17.3707 8.21532 18 7.43836 18H6.59461H5.47313C5.42039 18 5.36766 17.9965 5.31492 17.993C5.27274 17.9965 5.23055 18 5.18836 18H4.62586C3.84891 18 3.21961 17.3707 3.21961 16.5938V12.6562C3.21961 12.6246 3.21961 12.5895 3.22313 12.5578V10.1109H2.09461C1.4618 10.1109 0.969612 9.61875 0.969612 8.98242C0.969612 8.66602 1.07508 8.38477 1.32117 8.13867L10.3352 0.28125C10.5813 0.0351562 10.8626 0 11.1087 0C11.3548 0 11.636 0.0703125 11.847 0.246094L20.8259 8.13867C21.1071 8.38477 21.2477 8.66602 21.2126 8.98242Z" fill="#7165FF"/>
                  </svg>
                  Ambiente
                </h4>
                <div className="pet-info-grid">
                  {schedule.medicalData.environment.habitat && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Habitat</span>
                      <span className="pet-info-value">{getHabitatLabel(schedule.medicalData.environment.habitat)}</span>
                    </div>
                  )}
                  {schedule.medicalData.environment.housingType && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Tipo de moradia</span>
                      <span className="pet-info-value">{getHousingLabel(schedule.medicalData.environment.housingType)}</span>
                    </div>
                  )}
                  {schedule.medicalData.environment.contactWithRiskAreas !== null && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Contato com áreas de risco</span>
                      <span className="pet-info-value">{schedule.medicalData.environment.contactWithRiskAreas ? 'Sim' : 'Não'}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Reproduction */}
            {schedule.medicalData?.reproduction && (
              <div className="schedule-section">
                <h4 className="schedule-section-title">
                  <svg width="23" height="19" viewBox="0 0 23 19" fill="none" xmlns="http://www.w3.org/2000/svg" style={{verticalAlign: 'middle', marginRight: '8px'}}>
                    <path d="M6.1875 10.375C6.70458 10.375 7.2166 10.2732 7.69432 10.0753C8.17204 9.8774 8.6061 9.58736 8.97173 9.22173C9.33736 8.8561 9.6274 8.42204 9.82528 7.94432C10.0232 7.4666 10.125 6.95458 10.125 6.4375C10.125 5.92042 10.0232 5.4084 9.82528 4.93068C9.6274 4.45296 9.33736 4.0189 8.97173 3.65327C8.6061 3.28764 8.17204 2.9976 7.69432 2.79972C7.2166 2.60185 6.70458 2.5 6.1875 2.5C5.67042 2.5 5.1584 2.60185 4.68068 2.79972C4.20296 2.9976 3.7689 3.28764 3.40327 3.65327C3.03764 4.0189 2.7476 4.45296 2.54972 4.93068C2.35185 5.4084 2.25 5.92042 2.25 6.4375C2.25 6.95458 2.35185 7.4666 2.54972 7.94432C2.7476 8.42204 3.03764 8.8561 3.40327 9.22173C3.7689 9.58736 4.20296 9.8774 4.68068 10.0753C5.1584 10.2732 5.67042 10.375 6.1875 10.375ZM12.375 6.4375C12.375 9.47148 10.1918 11.9957 7.3125 12.523V13.75H8.4375C9.05977 13.75 9.5625 14.2527 9.5625 14.875C9.5625 15.4973 9.05977 16 8.4375 16H7.3125V17.125C7.3125 17.7473 6.80977 18.25 6.1875 18.25C5.56523 18.25 5.0625 17.7473 5.0625 17.125V16H3.9375C3.31523 16 2.8125 15.4973 2.8125 14.875C2.8125 14.2527 3.31523 13.75 3.9375 13.75H5.0625V12.523C2.1832 11.9957 0 9.47148 0 6.4375C0 3.02031 2.77031 0.25 6.1875 0.25C9.60469 0.25 12.375 3.02031 12.375 6.4375ZM9.55898 12.9273C10.2375 12.5723 10.8562 12.1152 11.3906 11.5773C12.0938 12.2277 13.0324 12.625 14.066 12.625C16.2422 12.625 18.0035 10.8637 18.0035 8.6875C18.0035 6.51133 16.2422 4.75 14.066 4.75C13.8129 4.75 13.5633 4.77461 13.3242 4.82031C13.152 4.06445 12.8672 3.3543 12.4805 2.70391C12.9832 2.57031 13.5141 2.5 14.0625 2.5C15.3633 2.5 16.5727 2.90078 17.5711 3.58984L18.2953 2.86563L17.1211 1.69141C16.8785 1.44883 16.8082 1.08672 16.9383 0.770312C17.0684 0.453906 17.3777 0.25 17.7188 0.25H21.6562C22.1238 0.25 22.5 0.626172 22.5 1.09375V5.03125C22.5 5.37227 22.2961 5.68164 21.9797 5.81172C21.6633 5.9418 21.3012 5.87148 21.0586 5.62891L19.8844 4.45469L19.1602 5.17891C19.8457 6.17734 20.25 7.3832 20.25 8.6875C20.25 12.1047 17.4797 14.875 14.0625 14.875C12.2871 14.875 10.6875 14.1262 9.55898 12.9273Z" fill="#7165FF"/>
                  </svg>
                  Reprodução
                </h4>
                <div className="pet-info-grid">
                  {schedule.medicalData.reproduction.hasMated !== null && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Já cruzou</span>
                      <span className="pet-info-value">{schedule.medicalData.reproduction.hasMated ? 'Sim' : 'Não'}</span>
                    </div>
                  )}
                  {schedule.medicalData.reproduction.hasPreviousLitters !== null && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Teve ninhadas</span>
                      <span className="pet-info-value">
                        {schedule.medicalData.reproduction.hasPreviousLitters ? `Sim (${schedule.medicalData.reproduction.litterCount || 0})` : 'Não'}
                      </span>
                    </div>
                  )}
                  {schedule.medicalData.reproduction.litterCount && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Quantidade de ninhadas</span>
                      <span className="pet-info-value">{schedule.medicalData.reproduction.litterCount}</span>
                    </div>
                  )}
                  {schedule.medicalData.reproduction.regularEstrousCycle !== null && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Ciclo estral regular</span>
                      <span className="pet-info-value">{schedule.medicalData.reproduction.regularEstrousCycle ? 'Sim' : 'Não'}</span>
                    </div>
                  )}
                  {schedule.medicalData.reproduction.hormonalChangesObserved && (
                    <div className="pet-info-item">
                      <span className="pet-info-label">Mudanças hormonais observadas</span>
                      <span className="pet-info-value">{schedule.medicalData.reproduction.hormonalChangesObserved}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Review */}
            {schedule.status === 'completed' && schedule.review && (
              <div className="schedule-section">
                <h4 className="schedule-section-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="#F59E0B" stroke="#F59E0B" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: 'middle', marginRight: '8px'}}>
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                  Avaliação do Cliente
                </h4>
                <div className="review-card">
                  <div className="review-header">
                    {schedule.review.user?.image ? (
                      <img 
                        src={schedule.review.user.image} 
                        alt={schedule.review.user.name}
                        className="review-user-avatar"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = placeholderImage;
                        }}
                      />
                    ) : (
                      <div className="schedule-user-avatar-placeholder">
                        {schedule.review.user?.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <div className="review-user-info">
                      <h4 className="review-user-name">{schedule.review.user?.name}</h4>
                      <span className="review-date">
                        {dayjs(schedule.review.createdAt).format('DD/MM/YYYY [às] HH:mm')}
                      </span>
                    </div>
                    <div className="review-rating">
                      {[...Array(5)].map((_, index) => (
                        <svg 
                          key={index} 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill={index < schedule.review.score ? '#F59E0B' : '#E5E7EB'}
                          stroke={index < schedule.review.score ? '#F59E0B' : '#D1D5DB'}
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                        </svg>
                      ))}
                      <span className="rating-value">{schedule.review.score.toFixed(1)}</span>
                    </div>
                  </div>
                  {schedule.review.content && (
                    <div className="review-body">
                      <p className="review-text">{schedule.review.content}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="schedule-modal-actions">
              {schedule.status === 'pending' && (
                <>
                  <button 
                    className="schedule-action-btn schedule-btn-reject"
                    onClick={handleRejectClick}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                    Recusar
                  </button>
                  <button 
                    className="schedule-action-btn schedule-btn-accept"
                    onClick={() => handleStatusChange('confirmed')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Aceitar
                  </button>
                </>
              )}
              <button 
                className="schedule-action-btn schedule-btn-call"
                onClick={handleCall}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                Ligar
              </button>
              <button 
                className="schedule-action-btn schedule-btn-whatsapp"
                onClick={handleWhatsApp}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </button>
            </div>
          </>
        ) : (
          <div className="schedule-modal-error">
            <p>Não foi possível carregar os detalhes do agendamento.</p>
          </div>
        )}
      </div>

      {/* Modal de Rejeição */}
      {showRejectModal && (
        <div className="reject-modal-overlay" onClick={handleRejectCancel}>
          <div className="reject-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Motivo da Recusa</h3>
            <p>Por favor, informe o motivo da recusa do agendamento:</p>
            <textarea
              className="reject-textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Digite o motivo da recusa..."
              rows="4"
              autoFocus
            />
            <div className="reject-modal-actions">
              <button className="reject-btn-cancel" onClick={handleRejectCancel}>
                Cancelar
              </button>
              <button className="reject-btn-confirm" onClick={handleRejectConfirm}>
                Confirmar Recusa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScheduleDetailsModal;
